import { useEffect, useMemo, useState } from "react";
import { ContainerWrapper, SpinWrapper } from "../../../app/app.style";
import { useFiltersContext } from "../../../contexts/filtersContext";
import Filters from "../../masterComponents/Filters";
import { DEFAULT_OPEN_KEYS_STATISTICS } from "../../../entities/entities";
import { App, AutoComplete, Row, Segmented, Space, Spin } from "antd";
import ActionButton from "../../masterComponents/actionButton";
import Icon from "../../masterComponents/Icon";
import { TbTableDown } from "react-icons/tb";
import { PHRASES_DICTIONARY } from "../../../entities/phrasesDictionary";
import {
  StyledAnalysisActions,
  StyledPrompt,
} from "../../tabs/analysisAgent/analysisAgent.style";
import { useFetchData } from "../../../hooks/useFetch/post";
import { LoadingOutlined } from "@ant-design/icons";
import { useFilterStructure } from "../../../hooks/useFilterStructure/useFilterStructure";
import { buildFilterStructure } from "../../../utils/filtersStructureHelpers";
import "./statistics.css";
import StatsList from "./statsList";
import { ClearOutlined } from "@ant-design/icons";
import { getPercentage } from "../../../utils";
import { useAttrs } from "../../../hooks/useEndpoints/useAttrs";

const Statistics = () => {
  const {
    appliedFilters,
    setAppliedFilters,
    ignoredFilter,
    setIgnoredFilter,
    updateFilter,
  } = useFiltersContext();

  const {
    availableFilters: availableStatisticsFilters,
    activeFilters,
    classNameFilterValues,
  } = useFilterStructure("statistics");

  const [attributeToAnalyze, setAttributeToAnalyze] = useState("");
  const [attrInputValue, setAttrInputValue] = useState("");

  const { message: messageApi } = App.useApp();

  const [format, setFormat] = useState("parquet");

  const { attributes, isLoading: isLoadingAttributes } = useAttrs({
    classNames: classNameFilterValues ?? [],
    operation: "intersection",
    enabled: classNameFilterValues.length <= 10,
    messageApi,
  });

  const attrsOptions = useMemo(
    () =>
      attributes?.map((attr) => ({
        value: attr.attr_code,
        label: attr.attr_descr,
      })) ?? [],
    [attributes],
  );

  useEffect(() => {
    if (
      attributeToAnalyze &&
      !attrsOptions.some((opt) => opt.value === attributeToAnalyze)
    ) {
      setAttributeToAnalyze("");
      setAttrInputValue("");
    }
  }, [attrsOptions]);

  const [
    fetchExportReport,
    {
      isLoading: isLoadingExportReport,
      data: exportReportData,
      error: exportReportError,
      clearError: clearExportReportError,
    },
  ] = useFetchData({
    query: "/@batch/reports/full-export",
    selectData: (data) => data,
  });

  useEffect(() => {
    if (exportReportData?.success && !isLoadingExportReport) {
      messageApi.success(PHRASES_DICTIONARY.SUCCESS.report_upload);
    }
  }, [isLoadingExportReport, messageApi]);

  useEffect(() => {
    if (exportReportError) {
      messageApi.error(exportReportError.message);
      clearExportReportError?.();
    }
  }, [exportReportError, messageApi]);

  const [
    fetchStatisticsData,
    {
      isLoading: isLoadingStatisticsData,
      data: statisticsData,
      error: statisticsDataError,
      clearError: clearStatisticsDataError,
    },
  ] = useFetchData({
    optionalArray: [classNameFilterValues],
    query: "/@batch/statistics/verification-by-class",
    selectData: (data) =>
      data.items
        .map((item) => ({
          ...item,
          key: item.class_name,
          characteristics: item.characteristics
            .map((child) => ({
              ...child,
              className: item.class_name,
              key: `${item.class_name} ${child.characteristic_name.attr_code}`,
              percentageUnverified: getPercentage(
                child.unverified_count?.value,
                child.total_count?.value,
              ),
              percentageVerified: getPercentage(
                child.verified_count?.value,
                child.total_count?.value,
              ),
              percentageValidated: getPercentage(
                child.validation_count?.value,
                child.total_count?.value,
              ),
            }))
            .sort((a, b) =>
              a.characteristic_name.attr_code?.localeCompare(
                b.characteristic_name.attr_code,
              ),
            ),
        }))
        .sort(
          (a, b) =>
            classNameFilterValues.indexOf(a.class_name) -
            classNameFilterValues.indexOf(b.class_name),
        ),
  });

  useEffect(() => {
    if (statisticsDataError) {
      messageApi.error(statisticsDataError.message);
      clearStatisticsDataError?.();
    }
  }, [statisticsDataError, messageApi]);

  const filterStructure = buildFilterStructure(activeFilters);

  const isInvalidLength = !(
    classNameFilterValues?.length && classNameFilterValues?.length < 11
  );

  return (
    <ContainerWrapper>
      <Filters
        appliedFilters={appliedFilters}
        setAppliedFilters={setAppliedFilters}
        currentAvailableFilters={availableStatisticsFilters}
        ignoredFilter={ignoredFilter}
        setIgnoredFilter={setIgnoredFilter}
        updateFilter={updateFilter}
        defaultOpenKeys={DEFAULT_OPEN_KEYS_STATISTICS}
      />
      <StyledAnalysisActions>
        <Space>
          Characteristic:
          <AutoComplete
            placeholder="Select Characteristic"
            style={{ width: 500 }}
            dropdownStyle={{ zIndex: 900 }}
            value={attrInputValue}
            options={attrsOptions}
            onChange={setAttrInputValue}
            onSelect={(val, option) => {
              setAttributeToAnalyze(val);
              setAttrInputValue(`${val} (${option?.label})`);
            }}
            onBlur={() => {
              if (attributeToAnalyze && attrInputValue !== attributeToAnalyze) {
                const option = attrsOptions.find(
                  (o) => o.value === attributeToAnalyze,
                );
                if (option) {
                  setAttrInputValue(`${option.value} (${option.label})`);
                }
              }
            }}
            optionRender={(option) => (
              <span>
                {option.value} ({option.label})
              </span>
            )}
            filterOption={(inputValue, option) => {
              if (!option?.value || !option?.label) return false;
              const search = inputValue.toLowerCase();
              return (
                String(option.value).toLowerCase().includes(search) ||
                String(option.label).toLowerCase().includes(search)
              );
            }}
            popupRender={(menu) =>
              isLoadingAttributes ? (
                <SpinWrapper>
                  <Spin indicator={<LoadingOutlined />} />
                </SpinWrapper>
              ) : (
                menu
              )
            }
          />
          <ActionButton
            className="primary-btn-color"
            icon={<Icon icon={ClearOutlined} />}
            onClick={() => {
              setAttributeToAnalyze("");
              setAttrInputValue("");
            }}
          />
        </Space>
        <Space>
          {isInvalidLength && (
            <StyledPrompt>{PHRASES_DICTIONARY.statistics_prompt}</StyledPrompt>
          )}
          <ActionButton
            text="GET"
            className="primary-btn-color"
            icon={<Icon icon={TbTableDown} />}
            iconPosition="end"
            disabled={isInvalidLength}
            onClick={() => {
              fetchStatisticsData(
                { batch: "FULL_JDE_EQUIPMENTS" },
                {
                  attribute_name: attributeToAnalyze,
                  filters: filterStructure,
                },
              );
            }}
            loading={isLoadingStatisticsData}
          />
        </Space>
      </StyledAnalysisActions>
      <StatsList statisticsData={statisticsData} />
      {/* <Row style={{ gap: "var(--spacing-xs)", alignItems: "center" }}>
        <Segmented
          options={["parquet", "csv"]}
          value={format}
          onChange={setFormat}
          style={{
            border: "1px solid rgb(0, 159, 152)",
            background: "white",
          }}
        />
        <ActionButton
          className="primary-btn-color"
          text={"Get all finals"}
          styles={{ width: 150 }}
          onClick={() =>
            fetchExportReport({ batch: "FULL_JDE_EQUIPMENTS", format }, {})
          }
        />
      </Row> */}
    </ContainerWrapper>
  );
};
export default Statistics;
